import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  colors,
  FormControl,
  FormLabel,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CreateCourtPayloadType } from "../../types/generalTypes";
import { getCompanyData } from "../../utils/generalUtil";
import { createCourt } from "../../api/CourtAPI";

import Futvolei from "../../icons/futvolei.svg";
import Volei from "../../icons/volei.svg";
import BeachTennis from "../../icons/beachTennis.svg";
import Soccer from "../../icons/soccer.png";

interface AddCourtProps {
  closeModal: () => void;
  fetchCourts: () => Promise<void>;
}

const MODALITIES = [
  { id: 0, label: "Beach Tennis", icon: BeachTennis },
  { id: 1, label: "Futvôlei", icon: Futvolei },
  { id: 2, label: "Vôlei", icon: Volei },
  { id: 3, label: "Futebol", icon: Soccer },
  { id: 4, label: "Tênis", icon: BeachTennis },
];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", md: "600px" },
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
  maxHeight: "90vh",
};

const AddCourt = ({ closeModal, fetchCourts }: AddCourtProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedModalities, setSelectedModalities] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const { companyId } = getCompanyData();
  const isDisabled =
    name === "" ||
    description === "" ||
    selectedModalities.length === 0 ||
    loading;

  const toggleModality = (id: number) => {
    setSelectedModalities((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  console.log(selectedModalities);

  const onSaveCourt = async () => {
    if (companyId) {
      setLoading(true);
      try {
        const data: CreateCourtPayloadType = {
          company_id: companyId,
          description: description,
          name: name,
          modality: selectedModalities.join(","),
          image_url: "",
        };

        await createCourt(data);
        await fetchCourts();
        closeModal();
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal open onClose={closeModal}>
      <Box sx={style}>
        <Stack spacing={3}>
          <Typography sx={{ fontWeight: 600 }} fontSize="18px" color="#22303E">
            Cadastrar Quadra
          </Typography>

          <FormControl>
            <FormLabel sx={{ mb: 1 }}>Nome da Quadra</FormLabel>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              disabled={loading}
            />
          </FormControl>

          <FormControl>
            <FormLabel sx={{ mb: 1 }}>Descrição</FormLabel>
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              disabled={loading}
            />
          </FormControl>

          <FormControl>
            <FormLabel sx={{ mb: 1 }}>Modalidades</FormLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {MODALITIES.map((item) => {
                const isSelected = selectedModalities.includes(item.id);
                return (
                  <Box
                    key={item.id}
                    onClick={() => !loading && toggleModality(item.id)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100px",
                      height: "100px",
                      border: `2px solid ${isSelected ? colors.blue[500] : "#E0E0E0"}`,
                      borderRadius: "8px",
                      cursor: loading ? "default" : "pointer",
                      backgroundColor: isSelected
                        ? `${colors.blue[50]}10`
                        : "transparent",
                      transition: "0.2s",
                      "&:hover": { borderColor: colors.blue[300] },
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.label}
                      style={{
                        width: "32px",
                        height: "32px",
                        filter: isSelected ? "none" : "grayscale(100%)",
                        opacity: isSelected ? 1 : 0.6,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? colors.blue[700] : "#666",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, pt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              sx={{ background: "#ffffff", color: "#3e3e3e", fontWeight: 550 }}
              onClick={closeModal}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={isDisabled}
              sx={{
                background: colors.blue[500],
                fontWeight: 550,
                "&:disabled": { background: "#ccc" },
              }}
              onClick={onSaveCourt}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Cadastrar"
              )}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default AddCourt;
